import { ClockIcon } from '@heroicons/react/20/solid';
import Image from 'next/image';
import { useMemo } from 'react';
import type { TipDetailDto, TipsterDetailDto } from '@/api/backoffice-schemas';
import type { TeamMatchDetailDto } from '@/api/backoffice-zod-schemas';
import { cn } from '@/utils/cn';
import { getGameFromBackofficeVideoGame } from '@/utils/gameMappers';
import { formatMatchDatetime } from '@/utils/matches';
import { getExpectedWinner } from '@/utils/tips';
import { getTipsterDisplayName } from '@/utils/tipster';
import { Button } from '../button';
import { GameIcon } from '../GameIcon';
import { AvatarIcon } from '../icons/meta/AvatarIcon';
import { TeamLogo } from '../TeamLogo';

// Types
interface VerticalTipCardProps {
  data: TipDetailDto;
  isHighlighted?: boolean;
  className?: string;
}

export const VerticalTipCard = ({
  data,
  isHighlighted,
  className,
}: VerticalTipCardProps) => {
  const teamA = data.match?.teamMatches?.[0];
  const teamB = data.match?.teamMatches?.[1];
  const league = data.match?.league;
  const game = getGameFromBackofficeVideoGame(data.match?.videoGame?.slug);
  const expectedWinner = getExpectedWinner({ tip: data });
  const teamInfoDirection = isHighlighted ? 'horizontal' : 'vertical';

  return (
    <div
      className={cn(
        'flex h-full min-h-96 flex-1 flex-col justify-between gap-3 rounded-lg border-t-2 border-t-light-blue bg-card-bg p-4',
        isHighlighted &&
          'border border-light-blue-o25 bg-transparent lg:border-0 lg:border-t-2 lg:border-t-light-blue lg:bg-highlight-card-gradient',
        className
      )}
    >
      <div className="flex flex-1 flex-col gap-3">
        <div className="flex justify-center">
          <GameIcon className="size-7" game={game} />
        </div>

        <div className="mb-4 h-[32px]">
          {league && (
            <div className="flex items-center justify-center gap-3">
              {league?.imageUrl && (
                <Image
                  alt="league logo"
                  className="block rounded-lg"
                  height={32}
                  src={league?.imageUrl}
                  width={32}
                />
              )}
              <span className="font-semibold text-xs">{league.name}</span>
            </div>
          )}
        </div>

        <div className="mb-3 flex items-center justify-center gap-2">
          <ClockIcon className="size-4 text-blue-gray" />
          <p className="font-medium text-sm leading-4">
            {formatMatchDatetime(data.match?.beginAt)}
          </p>
        </div>
      </div>

      <div className="my-4 grid w-full grid-cols-[1fr_auto_1fr] items-center gap-2">
        <div className="flex justify-center">
          {teamA && <TeamInfo direction={teamInfoDirection} team={teamA} />}
        </div>
        <div className="flex items-center justify-center">
          <div className="flex size-[25px] items-center justify-center rounded-full bg-blue-gray font-semibold text-dark-gray text-xs">
            vs
          </div>
        </div>
        <div className="flex justify-center">
          {teamB && <TeamInfo direction={teamInfoDirection} team={teamB} />}
        </div>
      </div>

      {expectedWinner && (
        <p className="mb-2 font-bold text-sm">
          {expectedWinner.name} will win!
        </p>
      )}

      {data.description && (
        <p className="mb-5 line-clamp-4 flex-1 text-gray text-sm">
          {data.description}
        </p>
      )}

      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <TipsterInfo tipster={data.tipster} />
        </div>
        <Button
          as="link"
          className="w-full"
          href={`/tips/${data.slug}`}
          size="lg"
          variant="outlineWithBg"
        >
          View more details
        </Button>
      </div>
    </div>
  );
};

interface TeamInfoProps {
  team: TeamMatchDetailDto;
  direction?: TeamInfoDirection;
}

type TeamInfoDirection = 'horizontal' | 'vertical';

const TeamInfo = ({ team, direction = 'vertical' }: TeamInfoProps) => {
  const { team: teamData } = team;

  if (!teamData) {
    return null;
  }

  const { imageUrl, name } = teamData;

  const directionStyles: Record<TeamInfoDirection, string> = {
    horizontal: 'flex-row',
    vertical: 'flex-col justify-center',
  };

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-2 text-center',
        directionStyles[direction]
      )}
    >
      {imageUrl && (
        <div className="flex size-[45px] items-center justify-center p-1">
          <TeamLogo name={name} size={45} url={imageUrl} />
        </div>
      )}
      <span className="max-w-36 font-medium text-sm">{name}</span>
    </div>
  );
};

const TipsterInfo: React.FC<{
  tipster?: TipsterDetailDto;
}> = ({ tipster }) => {
  const name = useMemo(() => {
    return getTipsterDisplayName(tipster);
  }, [tipster]);
  return (
    <div className="flex flex-row items-center gap-1">
      {tipster?.profilePhoto ? (
        <Image
          alt="Tipster"
          className="rounded-full object-cover"
          height={24}
          src={tipster.profilePhoto}
          width={24}
        />
      ) : (
        <AvatarIcon
          className="flex items-center justify-center text-blue-gray"
          desc="Tipster avatar icon"
          title="Tipster"
        />
      )}
      <p className="font-normal text-xs uppercase leading-4">{name}</p>
    </div>
  );
};
